import React from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { ADMIN_COLORS } from '../../theme';

/* Shared chrome tokens, matching the builder header and properties rail so the
   dialogs read as part of the same product rather than stock MUI modals.
   Lives here (rather than in ThemeEditor) because the Advanced Settings modal
   renders the same section cards and text fields. */
export const THEME_UI = {
  accent: ADMIN_COLORS.accent,
  accentHover: ADMIN_COLORS.accentHover,
  accentSoft: ADMIN_COLORS.accentSoft,
  ring: ADMIN_COLORS.accentRing,
  divider: ADMIN_COLORS.divider,
  border: ADMIN_COLORS.border,
  borderStrong: ADMIN_COLORS.borderStrong,
  hover: ADMIN_COLORS.hover,
  fieldBg: ADMIN_COLORS.surface,
  sectionBg: ADMIN_COLORS.headBg,
  text: ADMIN_COLORS.text,
  textSecondary: ADMIN_COLORS.textSecondary,
  scrollbar: ADMIN_COLORS.scrollbar,
  scrollbarHover: ADMIN_COLORS.textFaint,
};

export const ColorSection: React.FC<{
  title: string;
  description?: string;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, description, headerAction, children }) => (

  <Box
    sx={{
      mb: 2,
      p: 2,
      border: '1px solid',
      borderColor: THEME_UI.border,
      borderRadius: 2.5,
      backgroundColor: THEME_UI.fieldBg,
    }}
  >
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: THEME_UI.text, mb: description ? 0.5 : 0 }}>
          {title}
        </Typography>
        {description && (
          <Typography
            sx={{
              fontSize: '11.5px',
              lineHeight: 1.6,
              color: 'text.secondary',
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
      {headerAction && <Box sx={{ flexShrink: 0 }}>{headerAction}</Box>}
    </Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 2,
      }}
    >
      {children}
    </Box>
  </Box>
);

export const LabeledText: React.FC<{
  label?: string;
  value: string;
  placeholder?: string;
  multiline?: boolean;
  minRows?: number;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ label, value, placeholder, multiline, minRows, disabled, onChange }) => (
  <Box sx={{ minWidth: 0 }}>
    {label && (
      <Typography
        sx={{
          mb: 0.75,
          fontSize: '12px',
          fontWeight: 600,
          color: disabled ? 'text.disabled' : THEME_UI.textSecondary,
          letterSpacing: 0.1,
        }}
      >
        {label}
      </Typography>
    )}
    <TextField
      size="small"
      fullWidth
      multiline={multiline}
      minRows={minRows}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        sx: {
          borderRadius: 2,
          fontSize: 13,
          backgroundColor: disabled ? THEME_UI.hover : THEME_UI.fieldBg,
          ...(multiline ? {} : { height: 38 }),
          '& .MuiOutlinedInput-notchedOutline': { borderColor: THEME_UI.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME_UI.borderStrong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: THEME_UI.accent,
            borderWidth: 1,
          },
        },
      }}
    />
  </Box>
);


export const SuccessPreview: React.FC<{
  title: string;
  message: string;
  buttonLabel: string;
  downloadLabel?: string;
}> = ({ title, message, buttonLabel, downloadLabel }) => {
  const marks: {
    top?: string;
    left?: string;
    right?: string;
    bottom?: string;
    size: number;
    color: string;
    char: string;
  }[] = [
    { top: '6%', left: '14%', size: 12, color: '#60a5fa', char: '✦' },
    { top: '2%', right: '10%', size: 9, color: '#e2e8f0', char: '✦' },
    { top: '18%', right: '2%', size: 7, color: '#22c55e', char: '●' },
    { bottom: '10%', left: '2%', size: 9, color: '#f472b6', char: '■' },
    { top: '10%', left: '0%', size: 9, color: '#3b82f6', char: '▲' },
    { bottom: '2%', right: '18%', size: 7, color: '#facc15', char: '●' },
  ];
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: THEME_UI.border,
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(16,24,40,0.07)',
        position: { md: 'sticky' },
        top: 0,
      }}
    >

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: 1.125,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid',
          borderColor: THEME_UI.divider,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.625, flexShrink: 0 }}>
          {['#ff5f57', '#febc2e', '#28c840'].map((dot) => (
            <Box key={dot} sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: dot }} />
          ))}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: 20,
            px: 1,
            borderRadius: 10,
            bgcolor: '#eef1f5',
            display: 'flex',
            alignItems: 'center',
            fontSize: 10,
            color: 'text.disabled',
            fontFamily: 'monospace',
          }}
        >
          your-form.com
        </Box>
        <Typography
          sx={{
            flexShrink: 0,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: 'text.disabled',
          }}
        >
          Live
        </Typography>
      </Box>

    <Box
      sx={{
        position: 'relative',
        p: 4,
        textAlign: 'center',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 20% 15%, rgba(59,130,246,0.10) 0, transparent 40%), radial-gradient(circle at 80% 10%, rgba(59,130,246,0.08) 0, transparent 35%)',
          pointerEvents: 'none',
        }}
      />
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            backgroundColor: '#f4f8ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
            position: 'relative',
          }}
        >
          {marks.map((m, i) => (
            <Box
              key={i}
              aria-hidden
              sx={{
                position: 'absolute',
                fontSize: m.size,
                color: m.color,
                top: m.top,
                left: m.left,
                right: m.right,
                bottom: m.bottom,
              }}
            >
              {m.char}
            </Box>
          ))}
          <Box
            sx={{
              width: 66,
              height: 66,
              borderRadius: '50%',
              backgroundColor: '#22c55e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(34,197,94,0.35)',
            }}
          >
            <Box component="svg" viewBox="0 0 24 24" sx={{ width: 32, height: 32 }}>
              <path
                d="M5 13l4 4L19 7"
                fill="none"
                stroke="#ffffff"
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Box>
          </Box>
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1 }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 3 }}>
          {message}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {downloadLabel && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              width: '100%',
              borderRadius: '999px',
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              border: '1px solid #e2e5ea',
              color: '#111111',
              backgroundColor: '#ffffff',
            }}
          >
            <Box component="svg" viewBox="0 0 24 24" sx={{ width: 15, height: 15, opacity: 0.75 }}>
              <path
                d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.9}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Box>
            {downloadLabel}
          </Box>
        )}

        <Box
          sx={{
            width: '100%',
            borderRadius: '999px',
            py: 1,
            fontSize: 13,
            fontWeight: 600,
            backgroundColor: '#111111',
            color: '#ffffff',
          }}
        >
          {buttonLabel}
        </Box>
        </Box>
      </Box>
    </Box>
    </Box>
  );
};
